import type { DirectHotelProvider } from '@/interfaces';
import type { VisBookWebproductPrice, VisBookWebproductProperties } from '@/server/integration/visbook/visbookResponseTypes';
import hcDisplayValues from '../../assets/displayValues.json';

const VISBOOK_CANCELLATION_PROP = 'is_cancellation_included';
const VISBOOK_BREAKFAST_PROP = 'is_breakfast_included';

/*
 * VisBook Test IDs:
 *
 * 5252 - test payment using test cards from https://shop.nets.eu/web/partners/test-cards
 * 5358 - min 3 nights, max 8, opens sept 1st 2021
 * 6706 - is_breakfast_included=1 & is_cancellation_included=1
 * 6057 - payment by invoice
 * 6058 - no payment
 * 6614 - partial payment (amount: 500)
 * 7891 - partial payment (50%)
 */

// Smarthotel (4 + 2 stk)
const hcToSmarthotelMap = {
    // 1126375: 6579, // Smarthotel Forus
    // 2670685: 6585, // Smarthotel Hammerfest
    // 1976120: 6583, // Smarthotel Oslo
    // 1875311: 6584, // Smarthotel Tromsø
    // 2992325: 0, // Banken Hotel (Haugesund)
    // 3213711: 0, // Bergen Harbour Hotel
};

// Citybox (3 stk + Tallinn)
const hcToCitiboxMap = {
    1708313: 6538, // Citybox Bergen
    4327377: 8116, // Citybox Danmarksplass
    1208022: 6499, // Citybox Oslo
    1519892: 7519, // Citybox Lite Kristiansand
};
if (process.env.NODE_ENV === 'development') {
    hcToCitiboxMap[1940307] = 5252; // Scandic Ørnen (Bergen)
    hcToCitiboxMap[1736567] = 777; // Henningsvær Bryggehotell
    hcToCitiboxMap[1711584] = 6058; // Reine Rorbuer
    hcToCitiboxMap[1208021] = 6614; // Hotell Bondeheimen (Oslo)
    hcToCitiboxMap[1412816] = 7891; // P-Hotels Oslo
}

// Classic Norway (16 stk)
const hcToClassicNorwayMap = {
    // 2670290: 0, // Ona Havstuer
    // 4479115: 0, // Ramme Fjordhotell
    // 1710556: 0, // Hotell Utsikten Geiranger
    // 1708971: 0, // Gloppen Hotell
    // 1869015: 0, // Sagafjord Hotell
    // 3097523: 0, // Valldal Fjordhotell
    1713493: 6318, // Hustadvika Gjestegård
    // 1502403: 0, // Håholmen Havstuer
    // 1744651: 0, // Nyvågar Rorbuhotell
    // 1709809: 0, // Angvik Gamle Handelssted
    // 1711161: 0, // Hotell Refsnes Gods
    // 1565760: 0, // Hotell Molde Fjordstuer
    // 1710500: 0, // Strand Hotel Fevik
    1712733: 7256, // Finnøy Havstuer
    // 1736567: 0, // Henningsvær Bryggehotell
    // 1711584: 0, // Reine Rorbuer
    1710556: 7214, // Hotell Utsikten
};

const hcToRondaneHoyfjellshotellMap = {
    1708848: 6772, // Rondane Høyfjellshotell
    3410297: 6778, // Rondane Hytter og Leiligheter
};

const hcToSkaslienMap = {
    1736711: 6789, // Skaslien Gjestgiveri
};

const hcToFjaerlandMap = {
    // 1764504: 6790, // Fjærland Fjordstue
};

const hcToMarkenGjestehusMap = {
    // 1701539: 6837, // Marken Gjestehus
};

const hcToBookLofotenMap = {
    2670379: 6994, // Lofoten Arctic Hotel / Lofoten_Arctic_Henningsvaer_Hotel
    3064805: 6993, // Lofoten Arctic Hotel Knusarn / Henningsvaer_Vertshus
};

const hcToBykjedalstunetMap = {
    1301211: 6995, // Byrkjedalstunet
};

const hcToVisBookMap = {
    ...hcToSmarthotelMap,
    ...hcToCitiboxMap,
    ...hcToClassicNorwayMap,
    ...hcToRondaneHoyfjellshotellMap,
    ...hcToSkaslienMap,
    ...hcToFjaerlandMap,
    ...hcToMarkenGjestehusMap,
    ...hcToBookLofotenMap,
    ...hcToBykjedalstunetMap,
};

export const hasVisBookMapping = (hotelId: number): boolean => Object.keys(hcToVisBookMap).includes(String(hotelId));

export const getVisBookId = (hotelId: number): number => hcToVisBookMap[hotelId];

export const getProviderForVisBookId = (webEntityId: number): DirectHotelProvider => {
    if (Object.values(hcToSmarthotelMap).includes(webEntityId)) {
        return 'SMARTHOTEL';
    }
    if (Object.values(hcToCitiboxMap).includes(webEntityId)) {
        return 'CITYBOX';
    }
    if (Object.values(hcToClassicNorwayMap).includes(webEntityId)) {
        return 'CLASSICNORWAY';
    }
    if (Object.values(hcToRondaneHoyfjellshotellMap).includes(webEntityId)) {
        return 'RONDANEHFH';
    }
    if (Object.values(hcToSkaslienMap).includes(webEntityId)) {
        return 'SKASLIEN';
    }
    if (Object.values(hcToFjaerlandMap).includes(webEntityId)) {
        return 'FJAERLANDFJORDSTUE';
    }
    if (Object.values(hcToMarkenGjestehusMap).includes(webEntityId)) {
        return 'MARKENGJESTEHUS';
    }
    if (Object.values(hcToBookLofotenMap).includes(webEntityId)) {
        return 'BOOKLOFOTEN';
    }
    if (Object.values(hcToBykjedalstunetMap).includes(webEntityId)) {
        return 'BYRKJEDALSTUNET';
    }
    return undefined;
};

export const isVisBookBreakfastIncluded = (props: VisBookWebproductProperties, price: VisBookWebproductPrice): boolean =>
    props[VISBOOK_BREAKFAST_PROP]?.value === '1' ||
    price.pensionType === 'halfBoard' ||
    price.pensionType === 'fullBoard' ||
    price.pensionType === 'withBreakfast';

export const isVisBookCancellationIncluded = (props: VisBookWebproductProperties): boolean =>
    props[VISBOOK_CANCELLATION_PROP]?.value === '1';

export const getInclusionsForVisBookProvider = (
    directHotelProvider: DirectHotelProvider,
    providerProperties: VisBookWebproductProperties,
    price: VisBookWebproductPrice,
): readonly string[] => {
    const inclusions = [];

    if (isVisBookBreakfastIncluded(providerProperties, price)) {
        inclusions.push(hcDisplayValues.hotelPageCustomFilters.breakfastIncluded);
    }

    if (directHotelProvider === 'CITYBOX') {
        inclusions.push('Selvbetjent innsjekking');
    }

    return inclusions;
};
